/**
 * Bulk Order List Component
*/

class BulkOrderList extends HTMLElement {
  static SELECTORS = {
    container: "[data-bulk-order-list]",
    tableBody: "[data-bulk-order-list-body]",
    mobileList: "[data-bulk-order-mobile-list]",
    totalCount: "[data-total-count]",
    totalPrice: "[data-total-price]",
    addAllButton: "[data-add-all-to-cart]",
    quantityInput: "[data-bulk-order-quantity-input]",
    quantityMinus: "[data-bulk-order-quantity-minus]",
    quantityPlus: "[data-bulk-order-quantity-plus]",
    variantRow: "[data-variant-id]",
    pagination: "[data-bulk-order-pagination]",
  };

  static CLASSES = {
    loading: "is-loading",
    maxReached: "bulk-order-list__quantity-input--max-reached",
  };

  static TIMEOUTS = {
    inputDebounce: 300,
    maxFeedback: 1000,
    successMessage: 2000,
    partialMessage: 3000,
    cartDrawerDelay: 600,
  };

  constructor() {
    super();
    this.cartItems = new Map();
    this.isLoading = false;
    this.cacheElements();
    this.init();
  }

  cacheElements() {
    const { SELECTORS } = BulkOrderList;
    this.container = this.querySelector(SELECTORS.container);
    this.tableBody = this.querySelector(SELECTORS.tableBody);
    this.mobileList = this.querySelector(SELECTORS.mobileList);
    this.totalCountEl = this.querySelector(SELECTORS.totalCount);
    this.totalPriceEl = this.querySelector(SELECTORS.totalPrice);
    this.addAllButton = this.querySelector(SELECTORS.addAllButton);
  }

  init() {
    this.bindQuantityEvents();
    this.bindPaginationEvents();
    this.bindAddToCartEvent();
  }


  bindQuantityEvents() {
    const { SELECTORS } = BulkOrderList;

    this.addEventListener("click", (e) => {
      const minusBtn = e.target.closest(SELECTORS.quantityMinus);
      const plusBtn = e.target.closest(SELECTORS.quantityPlus);

      if (minusBtn || plusBtn) {
        const input = (minusBtn || plusBtn).parentElement.querySelector(SELECTORS.quantityInput);
        if (input) this.updateQuantity(input, minusBtn ? -1 : 1);
      }
    });

    this.addEventListener("change", (e) => {
      if (e.target.matches(SELECTORS.quantityInput)) {
        this.handleInputChange(e.target);
      }
    });

    this.addEventListener("input", (e) => {
      if (e.target.matches(SELECTORS.quantityInput)) {
        clearTimeout(e.target._inputTimeout);
        e.target._inputTimeout = setTimeout(() => {
          this.handleInputChange(e.target);
        }, BulkOrderList.TIMEOUTS.inputDebounce);
      }
    });
  }

  updateQuantity(input, delta) {
    const row = input.closest(BulkOrderList.SELECTORS.variantRow);
    if (!row) return;

    const currentValue = parseInt(input.value) || 0;
    const max = parseInt(input.getAttribute("max"));
    let newValue = Math.max(0, currentValue + delta);

    if (!isNaN(max)) {
      newValue = Math.min(newValue, max);
    }

    input.value = newValue;
    this.handleInputChange(input);
  }

  handleInputChange(input) {
    const row = input.closest(BulkOrderList.SELECTORS.variantRow);
    if (!row) return;

    const variantId = row.dataset.variantId;
    const price = parseInt(row.dataset.variantPrice) || 0;
    const max = parseInt(input.getAttribute("max"));
    let quantity = Math.max(0, parseInt(input.value) || 0);

    if (!isNaN(max) && quantity > max) {
      quantity = max;
      this.showMaxLimitFeedback(input);
    }

    input.value = quantity;

    if (quantity > 0) {
      this.cartItems.set(variantId, { quantity, price });
    } else {
      this.cartItems.delete(variantId);
    }

    this.updateTotals();
  }

  showMaxLimitFeedback(input) {
    const { maxReached } = BulkOrderList.CLASSES;
    input.classList.add(maxReached);
    setTimeout(() => input.classList.remove(maxReached), BulkOrderList.TIMEOUTS.maxFeedback);
  }


  updateTotals() {
    let totalCount = 0;
    let totalPrice = 0;

    this.cartItems.forEach(({ quantity, price }) => {
      totalCount++;
      totalPrice += quantity * price;
    });

    if (this.totalCountEl) {
      const template = this.totalCountEl.dataset.itemsText || "__COUNT__ items";
      this.totalCountEl.textContent = template.replace("__COUNT__", totalCount);
    }

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatMoney(totalPrice);
    }

    if (this.addAllButton) {
      this.addAllButton.disabled = totalCount === 0;
    }
  }

  formatMoney(cents) {
    const symbol = this.totalPriceEl?.dataset.currencySymbol || "€";
    return `${(cents / 100).toFixed(2).replace(".", ",")} ${symbol}`;
  }


  bindPaginationEvents() {
    this.addEventListener("click", async (e) => {
      const link = e.target.closest(".pagination a, .pagination-wrapper a");
      if (!link || link.classList.contains("is-disabled")) return;

      e.preventDefault();
      await this.loadPage(link.href);
    });
  }

  async loadPage(url) {
    const { SELECTORS, CLASSES } = BulkOrderList;

    try {
      this.container?.classList.add(CLASSES.loading);

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to load page");

      const doc = new DOMParser().parseFromString(await response.text(), "text/html");

      this.updateElement(this.tableBody, doc.querySelector(SELECTORS.tableBody));
      this.updateElement(this.mobileList, doc.querySelector(SELECTORS.mobileList));
      this.updateElement(this.querySelector(SELECTORS.pagination), doc.querySelector(SELECTORS.pagination));

      this.restoreQuantities();
      window.history.pushState({}, "", url);
      this.container?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (error) {
      console.error("Error loading page:", error);
    } finally {
      this.container?.classList.remove(CLASSES.loading);
    }
  }

  updateElement(target, source) {
    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  restoreQuantities() {
    const { SELECTORS } = BulkOrderList;
    this.cartItems.forEach(({ quantity }, variantId) => {
      this.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach((row) => {
        const input = row.querySelector(SELECTORS.quantityInput);
        if (input) input.value = quantity;
      });
    });
  }


  bindAddToCartEvent() {
    this.addAllButton?.addEventListener("click", () => this.addAllToCart());
  }

  async addAllToCart() {
    if (this.cartItems.size === 0 || this.isLoading) return;

    const items = this.prepareItems();
    if (items.length === 0) return;

    const buttonState = this.setButtonLoading(true);

    try {
      const response = await this.postToCart(items);

      // Handle quantity limit error - try adding one by one
      if (response.status === 422) {
        const text = await response.text();
        if (text.includes("maximale Menge")) {
          const result = await this.addItemsOneByOne(items);
          if (result.successCount > 0) {
            this.handlePartialSuccess(result, buttonState.originalText);
            return;
          }
          throw new Error("No items could be added");
        }
        throw new Error(text);
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();
      this.handleSuccess(data, buttonState.originalText);
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.handleError(buttonState.originalText);
    } finally {
      this.isLoading = false;
    }
  }

  prepareItems() {
    const items = [];
    this.cartItems.forEach(({ quantity }, variantId) => {
      const id = parseInt(variantId, 10);
      if (quantity > 0 && id > 0) {
        items.push({ id, quantity });
      }
    });
    return items;
  }

  async postToCart(items) {
    return fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    });
  }

  async addItemsOneByOne(items) {
    let successCount = 0;
    const failedItems = [];

    for (const item of items) {
      try {
        const response = await this.postToCart([item]);
        if (response.ok) {
          successCount++;
          this.cartItems.delete(String(item.id));
        } else {
          failedItems.push(item);
        }
      } catch {
        failedItems.push(item);
      }
    }

    return { successCount, failedItems, totalItems: items.length };
  }


  setButtonLoading(loading) {
    this.isLoading = loading;
    const originalText = this.addAllButton.textContent;

    if (loading) {
      this.addAllButton.disabled = true;
      this.addAllButton.classList.add(BulkOrderList.CLASSES.loading);
    }

    return { originalText };
  }

  resetButton(keepDisabled = false) {
    this.addAllButton.disabled = keepDisabled;
    this.addAllButton.classList.remove(BulkOrderList.CLASSES.loading);
  }

  handleSuccess(data, originalText) {
    this.resetAllQuantities();
    this.cartItems.clear();
    this.updateTotals();

    this.dispatchCartRefresh(data);
    this.openCartDrawer();

    setTimeout(() => this.resetButton(originalText), BulkOrderList.TIMEOUTS.successMessage);
  }

  handleError(originalText) {
    alert("Fehler beim Hinzufügen der Artikel zum Warenkorb. Bitte versuchen Sie es erneut.");
    setTimeout(() => this.resetButton(originalText, false), BulkOrderList.TIMEOUTS.successMessage);
  }

  handlePartialSuccess({ failedItems }, originalText) {
    this.resetAllQuantities();

    // Keep only failed items in cart
    const newCartItems = new Map();
    failedItems.forEach((item) => {
      const variantId = String(item.id);
      const savedItem = this.cartItems.get(variantId);
      if (savedItem) {
        newCartItems.set(variantId, savedItem);
        // Restore input value
        this.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach((row) => {
          const input = row.querySelector(BulkOrderList.SELECTORS.quantityInput);
          if (input) input.value = savedItem.quantity;
        });
      }
    });
    this.cartItems = newCartItems;
    this.updateTotals();

    this.dispatchCartRefresh();
    this.openCartDrawer();

    setTimeout(() => {
      this.resetButton(originalText, this.cartItems.size === 0);
    }, BulkOrderList.TIMEOUTS.partialMessage);
  }


  dispatchCartRefresh(cart = null) {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: cart ? { cart } : undefined,
      })
    );
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    setTimeout(() => {
      if (typeof cartDrawer.show === "function") {
        cartDrawer.show();
      } else if (typeof cartDrawer.setAttribute === "function") {
        cartDrawer.setAttribute("open", "");
      }
    }, BulkOrderList.TIMEOUTS.cartDrawerDelay);
  }

  resetAllQuantities() {
    this.querySelectorAll(BulkOrderList.SELECTORS.quantityInput).forEach((input) => {
      input.value = 0;
    });
  }
}

customElements.define("bulk-order-list", BulkOrderList);
